import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg, LOGO_URL } from "@/lib/api";
import { toast } from "sonner";
import { usePageMeta } from "@/lib/seo";

export default function Login() {
  usePageMeta("Masuk", "Masuk ke akun Nawadah by NDH Official.", { noindex: true });
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ identifier: "", password: "", access_code: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.identifier, form.password, form.access_code);
      toast.success(`Selamat datang, ${user.name}!`);
      if (user.role === "owner" && !user.access_code_enabled) {
        toast.info("Aktifkan kode akses 6 digit untuk mengamankan akun pemilik");
        navigate("/admin/pengaturan?setupPin=1");
        return;
      }
      navigate(params.get("next") || (user.role === "owner" ? "/admin" : "/toko"));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-navy-dark px-4 py-16" data-testid="login-page">
      <div className="w-full max-w-md bg-navy border border-white/10 rounded-md p-8">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="NAWADAH by NDH Official" className="h-16 w-16 mx-auto rounded-full bg-white object-contain mb-4" />
          <h1 className="font-display text-2xl text-white">Masuk ke Akun Anda</h1>
          <p className="text-sm text-slate-400 mt-2">Akses toko online &amp; riwayat pesanan Anda</p>
        </div>
        {error && <p className="mb-4 text-sm text-red-400 bg-red-400/10 border border-red-400/30 rounded-md px-4 py-3" data-testid="login-error">{error}</p>}
        {params.get("passwordChanged") === "1" && !error && <p className="mb-4 text-sm text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 rounded-md px-4 py-3" data-testid="password-changed-message">Kata sandi berhasil diubah. Semua sesi lama sudah ditutup. Silakan masuk memakai kata sandi baru.</p>}
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-gold block mb-2">No. Telepon / Nama Pengguna</label>
            <input
              required
              value={form.identifier}
              onChange={(e) => setForm({ ...form, identifier: e.target.value })}
              data-testid="login-identifier"
              className="w-full bg-[#040F1D] border border-white/15 rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              placeholder="0857-xxxx-xxxx"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-gold block mb-2">Kode Akses 6 Digit <span className="normal-case tracking-normal text-slate-400">(khusus pemilik)</span></label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={form.access_code}
              onChange={(e) => setForm({ ...form, access_code: e.target.value.replace(/\D/g, "").slice(0, 6) })}
              data-testid="login-access-code"
              className="w-full bg-[#040F1D] border border-white/15 rounded-md px-4 py-3 text-sm tracking-[0.45em] text-white focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              placeholder="••••••"
              autoComplete="one-time-code"
            />
            <p className="mt-1.5 text-[11px] text-slate-400">Pelanggan tidak perlu mengisi kolom ini.</p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-gold block mb-2">Kata Sandi</label>
            <input
              required
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="login-password"
              className="w-full bg-[#040F1D] border border-white/15 rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full px-6 py-3.5 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-60"
          >
            {loading ? "Memproses..." : "Masuk"}
          </button>
        </form>
        <p className="text-center text-sm text-slate-400 mt-6">
          Belum punya akun? Akun pelanggan hanya dibuatkan oleh pemilik toko —{" "}
          <Link to="/kontak" data-testid="login-to-contact" className="text-gold font-semibold hover:underline">
            hubungi kami
          </Link>
        </p>
      </div>
    </div>
  );
}
